import { BBDTag, Horizontal, MaterialIcon, type MaterialSymbolIcon, RangePicker, Texto } from '@gravitate-js/excalibrr'
import { Select, Tabs } from 'antd'

import type { InvoiceStatus } from '../../../../api/useFreight/types'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'

interface InvoiceFilterTabsProps {
  activeStatus: string
  setActiveStatus: (status: string) => void
  costRevenue: 'All' | 'Cost' | 'Revenue'
  setCostRevenue: (costRevenue: 'All' | 'Cost' | 'Revenue') => void
  sentDateRangeFilter: [Date | undefined, Date | undefined]
  handleDateRangeChange: (dates: [Date | undefined, Date | undefined]) => void
  statusCounts: Record<string, number>
  statusConfig: Partial<
    Record<
      InvoiceStatus,
      {
        icon: MaterialSymbolIcon
        title: string
        tagProps: {
          success?: boolean
          warning?: boolean
          error?: boolean
          theme1?: boolean
          theme2?: boolean
        }
      }
    >
  >
}

/**
 * Tabs component for filtering invoices by status with additional filters
 */
export function InvoiceFilterTabs({
  activeStatus,
  setActiveStatus,
  costRevenue,
  setCostRevenue,
  sentDateRangeFilter,
  handleDateRangeChange,
  statusCounts,
  statusConfig: tabStatusConfig,
}: InvoiceFilterTabsProps) {
  const { scopes } = useSecurityContext()
  const items = [
    {
      key: 'all',
      label: (
        <Horizontal verticalCenter horizontalCenter style={{ gap: '8px' }}>
          <MaterialIcon icon='list' />
          <Texto appearance='default'>All Open</Texto>
          <BBDTag style={{ width: 'fit-content' }}>{statusCounts.all || 0}</BBDTag>
        </Horizontal>
      ),
      children: null,
    },
    ...Object.entries(tabStatusConfig)
      .map(([status, config]) => {
        // Format the status key consistently with the statusCounts keys
        const statusKey = status.toLowerCase()
        // Create URL-friendly key (with dashes) for the tab key only
        const tabKey = statusKey.replace(/\s+/g, '-')

        return {
          key: tabKey,
          label: (
            <Horizontal verticalCenter horizontalCenter style={{ gap: '8px' }}>
              <MaterialIcon icon={config.icon} />
              <Texto appearance='default'>{config.title}</Texto>
              <BBDTag
                success={config.tagProps.success && statusCounts[statusKey] > 0}
                warning={config.tagProps.warning && statusCounts[statusKey] > 0}
                error={config.tagProps.error && statusCounts[statusKey] > 0}
                theme1={config.tagProps.theme1 && statusCounts[statusKey] > 0}
                theme2={config.tagProps.theme2 && statusCounts[statusKey] > 0}
                style={{ width: 'fit-content' }}
              >
                {statusCounts[statusKey] || 0}
              </BBDTag>
            </Horizontal>
          ),
          children: null,
        }
      })
      .filter((item) => item.key !== 'open'),
  ]

  return (
    <Tabs
      activeKey={activeStatus}
      onChange={setActiveStatus}
      items={items}
      className='px-4'
      style={{
        marginBottom: 0,
        backgroundColor: 'var(--bg-1)',
        borderTop: '1px solid var(--gray-200)',
        borderRight: '1px solid var(--gray-200)',
        borderLeft: '1px solid var(--gray-200)',
        borderRadius: '8px 8px 0 0',
      }}
      tabBarStyle={{
        marginBottom: 0,
      }}
      tabBarExtraContent={{
        right: (
          <Horizontal verticalCenter gap={8}>
            {!scopes.is_carrier && (
              <>
                <Texto appearance='hint'>Cost/Revenue: </Texto>
                <Select
                  size='small'
                  value={costRevenue}
                  onChange={setCostRevenue}
                  style={{ width: 120, marginRight: 16 }}
                  options={[
                    { value: 'All', label: 'All' },
                    { value: 'Cost', label: 'Cost' },
                    { value: 'Revenue', label: 'Revenue' },
                  ]}
                />
              </>
            )}
            {activeStatus === 'sent' && (
              <RangePicker
                inputKey='sent-date-range'
                dates={sentDateRangeFilter}
                onChange={handleDateRangeChange}
                placement='bottomLeft'
              />
            )}
          </Horizontal>
        ),
      }}
    />
  )
}
